import { Scene, Vector3 } from '@babylonjs/core';
import { CharacterConfig } from '../config/characters';
import { InputManager } from '../InputManager';
import { SplineConstraint } from './SplineConstraint';
import { Character, ICharacterInput } from './Character';
import { PlayerInput } from './PlayerInput';
import { SwitchableInput } from './SwitchableInput';
import { PhysicsManager } from './physics';
import { CameraController } from './CameraController';
import { logger } from '../core/Logger';
import type { CombatSystem } from './CombatSystem';
import { InputBuffer } from './InputBuffer';
import { CooldownTracker } from './CooldownTracker';
import type { AttackDef } from '../types/combat';
import { INPUT_BUFFER_WINDOW } from '../types/combat';

export interface NPCAIBehavior {
  update(deltaTime: number, npc: NPCController): void;
}

class NPCInput implements ICharacterInput {
  private movementInput: number = 0;
  private verticalInput: number = 0;
  private jumpInput: boolean = false;
  private attackInput: boolean = false;
  private cameraForward: Vector3 = new Vector3(0, 0, 1);
  private cameraRight: Vector3 = new Vector3(1, 0, 0);

  getMovementInput(): number {
    return this.movementInput;
  }

  getVerticalInput(): number {
    return this.verticalInput;
  }

  isJumpPressed(): boolean {
    return this.jumpInput;
  }

  isAttackPressed(): boolean {
    return this.attackInput;
  }

  getCameraForward(): Vector3 {
    return this.cameraForward;
  }

  getCameraRight(): Vector3 {
    return this.cameraRight;
  }

  // AI can set these values
  setMovementInput(value: number): void {
    this.movementInput = value;
  }

  setVerticalInput(value: number): void {
    this.verticalInput = value;
  }

  setJumpInput(value: boolean): void {
    this.jumpInput = value;
  }

  setAttackInput(value: boolean): void {
    this.attackInput = value;
  }

  setCameraVectors(forward: Vector3, right: Vector3): void {
    this.cameraForward = forward;
    this.cameraRight = right;
  }
}

export class NPCController extends Character {
  private npcInput: NPCInput;
  private playerInput: PlayerInput | null = null;
  private inputRouter: SwitchableInput;
  private aiBehavior: NPCAIBehavior | null = null;
  private storedAIBehavior: NPCAIBehavior | null = null;

  private inputManager: InputManager | null = null;
  private combatSystem: CombatSystem | null = null;

  private inputBuffer = new InputBuffer();
  private cooldowns = new CooldownTracker();
  private activeAttackId: string | null = null;
  private activeAttackDef: AttackDef | null = null;
  private wasAttackActive = false;

  private prevActionPressed = false;
  private prevAction2Pressed = false;
  private prevAction3Pressed = false;

  // One-shot warning flags to avoid log spam
  private _warnedNoCombatSystem = false;
  private _warnedNoInputManager = false;
  private _debuggedCombatInputs = false;

  private readonly npcSpeedMultiplier: number = 0.75;
  private readonly playerSpeedMultiplier: number = 1.0;

  constructor(
    scene: Scene,
    splineConstraint: SplineConstraint,
    characterConfig: CharacterConfig,
    npcId: string,
    physicsManager?: PhysicsManager
  ) {
    super(scene, splineConstraint, characterConfig, npcId, false, physicsManager);
    this.npcInput = new NPCInput();
    this.inputRouter = new SwitchableInput(this.npcInput);
    this.speedMultiplier = this.npcSpeedMultiplier; // NPC runs at 75% of player speed to avoid perfect matching
  }

  public enablePlayerControl(input: InputManager, camera: CameraController): void {
    this.playerInput = new PlayerInput(input, camera);
    this.inputManager = input;
  }

  public setCombatSystem(combatSystem: CombatSystem | null): void {
    this.combatSystem = combatSystem;
  }

  private handleCombatInputs(): void {
    // Debug: log once to verify function is called and check isPlayer
    if (!this._debuggedCombatInputs) {
      logger.combat.debug(`handleCombatInputs called for ${this.getId()}, isPlayer=${this.isPlayer}, combatSystem=${!!this.combatSystem}, inputManager=${!!this.inputManager}`);
      this._debuggedCombatInputs = true;
    }
    if (!this.isPlayer) return;
    if (!this.combatSystem) {
      // Only log once to avoid spam
      if (!this._warnedNoCombatSystem) {
        logger.core.warn(`[Combat] ${this.getId()} has no combatSystem set`);
        this._warnedNoCombatSystem = true;
      }
      return;
    }
    if (!this.inputManager) {
      if (!this._warnedNoInputManager) {
        logger.core.warn(`[Combat] ${this.getId()} has no inputManager set`);
        this._warnedNoInputManager = true;
      }
      return;
    }

    const now = performance.now() / 1000;

    // Track transitions from active -> idle to consume buffered input and start cooldowns
    const isAttackActive = this.combatSystem.hasActiveAttack(this.getId());
    if (this.wasAttackActive && !isAttackActive) {
      logger.core.log(`[Combat] Attack ended for ${this.getId()}, starting cooldowns, activeAttackDef=${this.activeAttackDef?.id}`);
      this.startCooldownsForLastAttack();
      this.consumeBufferedInput(now);
      this.activeAttackId = null;
      this.activeAttackDef = null;
    }
    this.wasAttackActive = isAttackActive;
    
    if (isAttackActive) {
      logger.core.log(`[Combat] Attack active for ${this.getId()}: ${this.activeAttackId}`);
    }

    // Rising-edge detection
    const actionPressed = this.inputManager.action;
    const action2Pressed = this.inputManager.action2;
    const action3Pressed = this.inputManager.action3;

    const bindings = this.characterConfig?.combatBindings;
    const combatAttacks = this.characterConfig?.combatAttacks;

    const tryStartBoundAttack = (bindingKey: 'action' | 'action2' | 'action3', fallbackId: string, risingEdge: boolean) => {
      if (!risingEdge) return;

      const attackId = bindings?.[bindingKey] ?? fallbackId;
      if (!combatAttacks) {
        logger.core.warn('No combatAttacks in character config');
        return;
      }
      const attackDef = combatAttacks[attackId];
      if (!attackDef) {
        logger.core.warn(`combatAttack '${attackId}' not found in character config`);
        return;
      }

      const onCooldown = this.cooldowns.isOnCooldown(attackId) || this.cooldowns.isGlobalCooldownActive();
      const activeAttack = this.combatSystem!.hasActiveAttack(this.getId());
      const canExit = this.animationController?.canExitCurrentState() ?? true;
      const exitTime = this.animationController?.getExitTime() ?? null;
      const normalizedTime = this.animationController?.getNormalizedTime() ?? 0;

      logger.combat.debug(`${this.getId()} attack request: ${attackId}, active=${activeAttack}, cooldown=${onCooldown}, canExit=${canExit}, normalizedTime=${normalizedTime.toFixed(2)}, exitTime=${exitTime?.toFixed(2) ?? 'none'}`);

      if (activeAttack || onCooldown) {
        logger.combat.debug(`${this.getId()} buffering input: ${attackId} (activeAttack=${activeAttack}, onCooldown=${onCooldown})`);
        this.inputBuffer.bufferInput(attackId, now, bindingKey);
        return;
      }

      this.activeAttackId = attackId;
      this.activeAttackDef = attackDef;

      logger.combat.debug(`${this.getId()} calling combatSystem.startAttack for ${attackId}`);
      this.combatSystem!.startAttack(this, attackDef, undefined, {
        binding: bindingKey,
        isHeld: () => this.getBindingHeldState(bindingKey)
      });
    };

    if (actionPressed && !this.prevActionPressed) {
      logger.combat.debug(`Action rising edge! Starting attack 'action' for ${this.getId()}`);
      tryStartBoundAttack('action', 'melee_horizontal', true);
    }

    if (action2Pressed && !this.prevAction2Pressed) {
      logger.combat.debug(`Action2 rising edge! Starting attack 'action2' for ${this.getId()}`);
      tryStartBoundAttack('action2', 'projectile_throw', true);
    }

    if (action3Pressed && !this.prevAction3Pressed) {
      logger.combat.debug(`Action3 rising edge! Starting attack 'action3' for ${this.getId()}`);
      tryStartBoundAttack('action3', 'projectile_throw_2', true);
    }

    this.prevActionPressed = actionPressed;
    this.prevAction2Pressed = action2Pressed;
    this.prevAction3Pressed = action3Pressed;
  }

  /**
   * Handle NPC combat inputs set by AI behavior.
   * Unlike player combat, NPCs use simplified attack logic.
   */
  private handleNPCCombatInputs(): void {
    if (!this.combatSystem) {
      // No combat system assigned - skip silently (normal for non-combat NPCs)
      return;
    }
    
    const attackPressed = this.npcInput.isAttackPressed();
    
    // Only trigger on rising edge (was not pressed, now pressed)
    if (attackPressed && !this.prevActionPressed) {
      console.log(`⚔️ [NPC] ${this.getId()} attack input RISING EDGE detected! combatSystem=${!!this.combatSystem}`);
      
      // Get default attack from character config
      const combatAttacks = this.characterConfig?.combatAttacks;
      const bindings = this.characterConfig?.combatBindings;
      const attackId = bindings?.action ?? 'punch'; // Default to 'punch'
      
      console.log(`⚔️ [NPC] ${this.getId()} combatAttacks=${Object.keys(combatAttacks || {}).join(',')}, bindings.action=${bindings?.action}, attackId=${attackId}`);
      
      if (!combatAttacks) {
        console.log(`⚔️ [NPC] ${this.getId()} NO combatAttacks in config!`);
        return;
      }
      
      const attackDef = combatAttacks[attackId];
      if (!attackDef) {
        console.log(`⚔️ [NPC] ${this.getId()} attack '${attackId}' NOT FOUND in config!`);
        return;
      }
      
      // Check if we can attack (no active attack, not on cooldown)
      const hasActiveAttack = this.combatSystem.hasActiveAttack(this.getId());
      const onCooldown = this.cooldowns.isOnCooldown(attackId);
      
      console.log(`⚔️ [NPC] ${this.getId()} hasActiveAttack=${hasActiveAttack}, onCooldown=${onCooldown}`);
      
      if (!hasActiveAttack && !onCooldown) {
        console.log(`⚔️ [NPC] ${this.getId()} STARTING ATTACK: ${attackId}`);
        this.combatSystem.startAttack(this, attackDef);
        this.activeAttackId = attackId;
        this.activeAttackDef = attackDef;
      }
    }
    
    // Track for edge detection
    this.prevActionPressed = attackPressed;
  }

  public setPlayerControlled(enabled: boolean): void {
    if (enabled) {
      if (!this.playerInput) {
        throw new Error('NPCController: enablePlayerControl() must be called before setPlayerControlled(true)');
      }

      if (this.aiBehavior) {
        this.storedAIBehavior = this.aiBehavior;
      }
      this.aiBehavior = null;
      this.npcInput.setMovementInput(0);
      this.npcInput.setJumpInput(false);
      this.npcInput.setAttackInput(false);

      this.inputRouter.setCurrent(this.playerInput);
      this.speedMultiplier = this.playerSpeedMultiplier;
      this.setIsPlayerControlled(true);

      // Initialize edge detectors so we don't fire immediately when switching leaders.
      if (this.inputManager) {
        this.prevActionPressed = this.inputManager.action;
        this.prevAction2Pressed = this.inputManager.action2;
        this.prevAction3Pressed = this.inputManager.action3;
      } else {
        this.prevActionPressed = false;
        this.prevAction2Pressed = false;
        this.prevAction3Pressed = false;
      }
      return;
    }

    this.inputRouter.setCurrent(this.npcInput);
    this.speedMultiplier = this.npcSpeedMultiplier;
    this.setIsPlayerControlled(false);

    // If caller doesn't immediately reassign AI, restore any previously stored behavior.
    if (!this.aiBehavior && this.storedAIBehavior) {
      this.aiBehavior = this.storedAIBehavior;
    }

    this.prevActionPressed = false;
    this.prevAction2Pressed = false;
    this.prevAction3Pressed = false;
  }

  private startCooldownsForLastAttack(): void {
    if (!this.activeAttackDef) return;
    const { cooldown, globalCooldown, id } = this.activeAttackDef;
    logger.core.log(`[Combat] Starting cooldowns for ${id}: cooldown=${cooldown}s, globalCooldown=${globalCooldown}s`);
    if (cooldown) {
      this.cooldowns.startCooldown(id, cooldown);
    }
    if (globalCooldown) {
      this.cooldowns.startGlobalCooldown(globalCooldown);
    }
  }

  private consumeBufferedInput(now: number): void {
    const buffered = this.inputBuffer.consumeBuffer(now, INPUT_BUFFER_WINDOW);
    if (!buffered) return;

    const combatAttacks = this.characterConfig?.combatAttacks;
    if (!combatAttacks) return;

    const attackDef = combatAttacks[buffered.action];
    if (!attackDef) return;

    if (this.cooldowns.isOnCooldown(buffered.action) || this.cooldowns.isGlobalCooldownActive()) {
      return;
    }

    this.activeAttackId = buffered.action;
    this.activeAttackDef = attackDef;

    this.combatSystem?.startAttack(this, attackDef, undefined, {
      binding: buffered.binding ?? 'action',
      isHeld: () => this.getBindingHeldState(buffered.binding ?? 'action')
    });
  }

  private getBindingHeldState(binding: 'action' | 'action2' | 'action3'): boolean {
    if (!this.inputManager) return false;
    switch (binding) {
      case 'action':
        return this.inputManager.action;
      case 'action2':
        return this.inputManager.action2;
      case 'action3':
        return this.inputManager.action3;
    }
  }

  public getInputDirection(): number {
    return this.inputRouter.getMovementInput();
  }
  
  protected isJumpPressed(): boolean {
    return this.inputRouter.isJumpPressed();
  }
  
  protected getCameraRight(): Vector3 {
    return this.inputRouter.getCameraRight();
  }

  public setAIBehavior(behavior: NPCAIBehavior | null): void {
    this.aiBehavior = behavior;
  }

  public override update(deltaTime: number): void {
    // Debug: log once to verify update is called
    if (!this._debuggedCombatInputs) {
      logger.npc.debug(`NPCController.update called for ${this.getId()}, isPlayer=${this.isPlayer}`);
    }
    
    // Skip AI and combat updates if dead - just run base update for animation
    if (this.getIsDead()) {
      this.npcInput.setMovementInput(0);
      super.update(deltaTime);
      return;
    }
    
    this.cooldowns.update(deltaTime);

    // Player-controlled combat input routing happens inside the controller,
    // not in main.ts.
    this.handleCombatInputs();

    if (!this.isPlayer) {
      // Update camera vectors to use spline tangent (NPCs don't have a camera)
      const tangent = this.getSplineTangent();
      // For NPCs, "right" is the tangent (movement direction)
      // This matches how player uses camera.right for movement
      this.npcInput.setCameraVectors(tangent, tangent);

      // Skip AI update if hit staggered - don't let AI override the stagger state
      if (!this.isHitStaggered()) {
        // Update AI behavior first
        if (this.aiBehavior) {
          this.aiBehavior.update(deltaTime, this);
        }

        // Handle NPC combat inputs (set by AI behavior)
        this.handleNPCCombatInputs();

        // IMPORTANT: The AI behavior sets movementInput directly (+1, -1, or 0)
        // This IS the intended facing direction along the spline.
        // We must set lastMovementDirection BEFORE calling super.update()
        // so Character.updateMovement() uses the correct facing direction.
        const movementInput = this.npcInput.getMovementInput();
        if (movementInput !== 0) {
          // AI wants to move in this direction - set it directly
          this.lastMovementDirection = movementInput > 0 ? 1 : -1;
        }
      } else {
        // Staggered: clear movement input so character stops
        this.npcInput.setMovementInput(0);
      }
    }

    // Then update character (physics, state machine, animation)
    // NOTE: super.update() already calls updatePhysics() and applyMovement()
    // which handles spline constraint. Do NOT call constrainToSpline() here
    // as it would cause double-projection and twitching!
    super.update(deltaTime);
  }
  
  public getSplineTangent(): Vector3 {
    // Use lastClosestIndex for consistent tangent calculation with updateMovement()
    const { projection } = this.splineConstraint.project(this.character.position, this.lastClosestIndex);
    return projection.tangent;
  }

  // Public API for AI to control the NPC
  public setMovementInput(value: number): void {
    this.npcInput.setMovementInput(value);
  }
  
  /**
   * Set which direction the NPC should face along the spline.
   * This is independent of movement - NPC can face one way while stopped.
   * @param direction 1 for positive tangent direction, -1 for negative
   */
  public override setFacingDirection(direction: number): void {
    // Call parent to update lastMovementDirection AND immediately apply rotation
    super.setFacingDirection(direction);
  }

  public setJumpInput(value: boolean): void {
    this.npcInput.setJumpInput(value);
  }

  public setAttackInput(value: boolean): void {
    this.npcInput.setAttackInput(value);
  }

  public setCameraVectors(forward: Vector3, right: Vector3): void {
    this.npcInput.setCameraVectors(forward, right);
  }

  /**
   * Force NPC debug meshes to always be visible (regardless of debug toggle).
   * Uses normal render layer (0x0FFFFFFF) instead of debug layer (0x20000000).
   */
  public forceDebugMeshesVisible(): void {
    const ALWAYS_VISIBLE_LAYER = 0x0FFFFFFF;
    
    if (this.debugFacingArrow) {
      this.debugFacingArrow.layerMask = ALWAYS_VISIBLE_LAYER;
      // Also update children (arrow shaft/head)
      this.debugFacingArrow.getChildMeshes().forEach(child => {
        child.layerMask = ALWAYS_VISIBLE_LAYER;
      });
    }
    
    if (this.debugTangentArrow) {
      this.debugTangentArrow.layerMask = ALWAYS_VISIBLE_LAYER;
      this.debugTangentArrow.getChildMeshes().forEach(child => {
        child.layerMask = ALWAYS_VISIBLE_LAYER;
      });
    }
    
    if (this.debugCurrentSegment) {
      this.debugCurrentSegment.layerMask = ALWAYS_VISIBLE_LAYER;
    }
    
    logger.npc.debug(`🔍 NPC ${this.characterId}: Debug meshes forced visible`);
  }
}
